import { Component, createContext, useContext } from 'react';
import Alert from 'react-bootstrap/Alert';
import { AuthenticationContext, authenticationStates } from '../msal/AuthenticationContext';
import { NotificationContext } from '../context/NotificationContext';
import * as apiCalls from '../logic/apiCalls';

export const TenantContext = createContext();

class TenantProviderInternal extends Component {
    constructor() {
        super();

        this.state = {
            isLoading: true,
            tenantId: "",        
            tenantLabel: "unknown",
            enabled: true,
            auditTrailRetentionDays: 365,
            manualLicenseCount: 0,
            totalLicenseCount: 0,
            manualSubscriptions: [],
            ownerSubscriptions: [],
            availableTenants: [],
            hasLoadedTenants: false,
            switchTenant: async (tenantId) => {
                await this.switchTenant(tenantId);
            },
            createTenant: async (tenantLabel) => {
                return await this.createTenant(tenantLabel);
            },

            showWarningWhenDisabled: () => { 
                return this.state.enabled ? null :
                    <div className='my-4'>
                        <Alert variant='warning' style={{ opacity: 1 }}>
                            Warning: Your account has been disabled!
                        </Alert>
                    </div>
            },
            refreshAll: async () => {
                this.refreshAll();
            },
            refreshTenant: async () => {
                this.refreshTenant();
            },
            refreshSubscriptions: async () => {
                this.refreshSubscriptions();
            }
        }
    }

    componentDidMount() {
        this.checkAndLoadTenants();
    }

    componentDidUpdate() {
        this.checkAndLoadTenants();
    }

    switchTenant = async (tenantId) => {
        if (tenantId === this.state.tenantId) {
            console.log(`Tenant ${tenantId} is already the current tenant. No need to switch.`);
            return;
        }

        var availableTenant = this.state.availableTenants.find(tenant => tenant.tenantId === tenantId);
        if (!availableTenant) {
            console.log(`Tenant ${tenantId} is not in the list of available tenants. Cannot switch.`);
            return;
        }

        console.log(`Switching to tenant ${tenantId}...`);
        this.setState({
            tenantId: tenantId,
            tenantLabel: availableTenant.tenantLabel,
        });
        await this.refreshAll();
    }

    checkAndLoadTenants = () => {
        // Only load tenants when authenticated
        const authState = this.props.authenticationContext.getAuthenticationState();
        if (authState !== authenticationStates.AUTHENTICATED) {
            return;
        }

        // Load available tenants if not already loaded
        if (!this.state.hasLoadedTenants) {
            this.getAvailableTenants();
        }
    }

    refreshAll = async () => {
        await this.getAvailableTenants();
    }

    refreshTenant = async () => {
        await this.getTenant(this.state.tenantId);
    }

    refreshSubscriptions = async () => {
        await this.getSubscriptions(this.state.tenantId);
    }

    getAvailableTenants = async () => {
        if (this._isLoadingTenants) {
            return;
        }
        this._isLoadingTenants = true;

        try {
            console.log("[getAvailableTenants] Invoking the back-end API to get all available tenants.");

            const availableTenants = await apiCalls.getAvailableTenants(this.props.authenticationContext);

            this.setState({
                availableTenants: availableTenants,
                hasLoadedTenants: true,
            });

            if (this.state.tenantId === "" && availableTenants.length > 0) {
                const firstTenant = availableTenants[0];
                console.log(`[getAvailableTenants] No tenant selected yet. Selecting the first available tenant: ${firstTenant.tenantId} (${firstTenant.tenantLabel})`);

                // After selecting a tenant, load tenant details and subscriptions
                // Pass tenantId directly since setState is asynchronous
                await this.getTenant(firstTenant.tenantId);
                await this.getSubscriptions(firstTenant.tenantId);
            }
        }
        catch (err) {
            console.log(`[getAvailableTenants] Error retrieving the available tenants: ${err}`);
            this.props.notificationContext.setCommunicationFailureNotification();
        }
        finally {
            this._isLoadingTenants = false;
        }
    }

    getTenant = async (tenantId) => {
        console.log("Start retrieving tenant...");
        
        try {
            console.log("[getTenant] Invoking the back-end API to get the tenant.");

            const tenant = await apiCalls.getTenant(this.props.authenticationContext, tenantId);

            this.setState({
                tenantId: tenant.tenantId,
                tenantLabel: tenant.tenantLabel,
                enabled: tenant.enabled,
                auditTrailRetentionDays: tenant.auditTrailRetentionDays
            });
        }
        catch (err) {
            console.log(`[getTenant] Error retrieving the tenant information: ${err}`);
            this.props.notificationContext.setCommunicationFailureNotification();
        }
        finally {
            this.setState({
                isLoading: false, 
            });
        }
    }

    getSubscriptions = async (tenantId) => {
        try {
            console.log("[getSubscriptions] Invoking the back-end API to get all subscriptions of this tenant.");

            const subscriptions = await apiCalls.getSubscriptions(this.props.authenticationContext, tenantId);

            const manualSubscriptions = subscriptions.filter(sub => sub.type === "Manual");
            const ownerSubscriptions = subscriptions.filter(sub => sub.type === "ManagedBySelf");
            
            const manualSubscriptionsCount = this.getSubscriptionsSum(manualSubscriptions);
            const ownerSubscriptionsCount = this.getSubscriptionsSum(ownerSubscriptions);

            const totalLicenseCount = manualSubscriptionsCount + ownerSubscriptionsCount;
            
            this.setState({
                manualLicenseCount: manualSubscriptionsCount,
                ownerLicenseCount: ownerSubscriptionsCount,
                totalLicenseCount: totalLicenseCount,
                
                manualSubscriptions: manualSubscriptions,
                ownerSubscriptions: ownerSubscriptions,
            });
        }
        catch (err) {
            // because of refresh no-longer setting error.
            console.log(`[getSubscriptions] Error retrieving the subscriptions: ${err}`);
            //this.props.notificationContext.setCommunicationFailureNotification();
        }
    }

    createTenant = async (tenantLabel) => {
        try {
            console.log(`[createTenant] Creating a new tenant with label '${tenantLabel}'...`);

            await apiCalls.createTenant(this.props.authenticationContext, tenantLabel);

            // Refresh the available tenants so the newly created tenant is picked up
            await this.getAvailableTenants();

            return true;
        }
        catch (err) {
            console.log(`[createTenant] Error creating tenant: ${err}`);
            this.props.notificationContext.setCommunicationFailureNotification();
            return false;
        }
    }

    getSubscriptionsSum = (subscriptions) => {
        let subscriptionSum = 0;
        for (var sub of subscriptions) {
            //if (sub.enabled) {
                subscriptionSum += sub.quantity;
            //}
        }
        return subscriptionSum;
    }

    render() {
        return (
            <TenantContext.Provider value={this.state}>
                {this.props.children}
            </TenantContext.Provider>
        )
    }
}

export const TenantProvider = (props) => {
    const authenticationContext = useContext(AuthenticationContext);       
    const notificationContext = useContext(NotificationContext);
  
    return (
        <TenantProviderInternal authenticationContext={authenticationContext} notificationContext={notificationContext} {...props}/>
    )
}